import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import { RichTextEditor } from "../../components/RichTextEditor";
import { SERVICE_AREAS } from "../../lib/stateOptions";
import { useFormStore } from "../../store";
import type { SupplierFields } from "../../types";

// Subset of SupplierFields that Step 2 manages.
type Step2Fields = Pick<
  SupplierFields,
  "new_servicearea" | "new_about" | "new_discountdetails"
>;

const SERVICE_AREA_OPTIONS: { value: number; label: (typeof SERVICE_AREAS)[number] }[] = [
  { value: 100000000, label: "North America" },
  { value: 100000001, label: "United States Only" },
  { value: 100000002, label: "Canada Only" },
];

function pickStep2(supplier: SupplierFields): Step2Fields {
  return {
    new_servicearea: supplier.new_servicearea,
    new_about: supplier.new_about,
    new_discountdetails: supplier.new_discountdetails,
  };
}

export default function Step2Products() {
  const supplier = useFormStore((s) => s.supplier);
  const setSupplier = useFormStore((s) => s.setSupplier);
  const supplierId = supplier.id;

  const { register, control, watch, reset } = useForm<Step2Fields>({
    defaultValues: pickStep2(supplier),
    mode: "onBlur",
  });

  useEffect(() => {
    reset(pickStep2(supplier));
  }, [supplierId, reset]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const sub = watch((values) => setSupplier(values as Partial<SupplierFields>));
    return () => sub.unsubscribe();
  }, [watch, setSupplier]);

  // RichTextEditor key — forces a remount of the Quill instance when a fresh
  // prefill arrives, since Quill is initialized from `initialHtml` only at
  // mount time.
  const editorKey = `editor-${supplierId ?? "new"}`;

  return (
    <div>
      <h2 className="brand-section-heading">
        <i className="fa-solid fa-box-open" aria-hidden="true" />
        Products &amp; Services
      </h2>

      <div className="mb-4">
        <label htmlFor="new_servicearea" className="form-label">
          Service Area / Country
        </label>
        <div className="input-group">
          <span className="input-group-text" aria-hidden="true">
            <i className="fa-solid fa-earth-americas" />
          </span>
          <select
            id="new_servicearea"
            className="form-select"
            {...register("new_servicearea", {
              setValueAs: (v) => (v === "" || v == null ? null : Number(v)),
            })}
          >
            <option value="">— Select —</option>
            {SERVICE_AREA_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-4">
        <label id="new_about-label" className="form-label">About</label>
        <Controller
          name="new_about"
          control={control}
          render={({ field }) => (
            <RichTextEditor
              key={`${editorKey}-about`}
              initialHtml={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ariaLabelledBy="new_about-label"
              placeholder="A short description of your business..."
            />
          )}
        />
      </div>

      <div className="mb-2">
        <label id="new_discountdetails-label" className="form-label">Benefits</label>
        <Controller
          name="new_discountdetails"
          control={control}
          render={({ field }) => (
            <RichTextEditor
              key={`${editorKey}-benefits`}
              initialHtml={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ariaLabelledBy="new_discountdetails-label"
              placeholder="Discounts, partnership perks, or other supplier-specific benefits..."
            />
          )}
        />
        <small className="form-text text-muted">
          Discounts, partnership perks, or other supplier-specific benefits to AFB franchisees.
        </small>
      </div>
    </div>
  );
}
